import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { AlertTriangle, XCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';

export function SuperUserEditingBanner() {
  const { isEditingAsDifferentUser, editingUserId, clearEditingState } = useAuth();
  const [userName, setUserName] = useState<string>('');
  const navigate = useNavigate();

  useEffect(() => {
    if (editingUserId) {
      fetchUserDetails(editingUserId);
    }
  }, [editingUserId]);

  const fetchUserDetails = async (userId: string) => {
    try {
      // Using user_roles table to get user information
      const { data: userRoleData, error: userRoleError } = await supabase
        .from('user_roles')
        .select('user_id')
        .eq('user_id', userId)
        .single();
      
      if (userRoleError) throw userRoleError;
      
      // Get user info from auth.users using admin API (service role)
      const { data: userData, error: userError } = await supabase.auth.admin.getUserById(userId);
      
      if (userError) throw userError;
      
      setUserName(userData?.user?.email || userId.substring(0, 8));
    } catch (error) {
      console.error('Error fetching user details:', error);
      // Fallback to shortened ID if we can't get the email
      setUserName(userId.substring(0, 8));
    }
  };

  const exitEditingMode = () => {
    clearEditingState();
    navigate('/admin/super-dashboard');
  };

  if (!isEditingAsDifferentUser()) {
    return null;
  }

  return (
    <div className="bg-amber-500 text-white py-2 px-4 flex items-center justify-between fixed bottom-0 left-0 right-0 z-50 shadow-lg">
      <div className="flex items-center">
        <AlertTriangle className="h-5 w-5 mr-2" />
        <span>
          <strong>Super User Editing Mode:</strong> You are currently editing dashboard for {userName}
        </span>
      </div>
      <Button 
        variant="outline" 
        size="sm" 
        className="bg-amber-600 hover:bg-amber-700 text-white border-white"
        onClick={exitEditingMode}
      >
        <XCircle className="h-4 w-4 mr-1" />
        Exit Editing Mode
      </Button>
    </div>
  );
} 